import { create, insertMultiple, search } from '@orama/orama';
import { markMatches, trimAroundMatch } from '~/modules/docs/search/highlight';
import type { DocsSearchResult, DocsSearchResultType, DocsSearchScope } from '~/modules/docs/search/types';
import type { GenComponentSchema, GenOperationSummary } from '~/modules/docs/types';

/** Everything the engine indexes for one docs page. */
export type EnginePage = {
  slug: string;
  title: string;
  description?: string;
  keywords?: string;
  breadcrumbs: string[];
  headings: { id: string; text: string }[];
  sections: { headingId: string | null; text: string }[];
};

/** Indexed fields; display-only fields ride along on the documents unindexed. */
const searchSchema = {
  kind: 'enum',
  pageId: 'string',
  title: 'string',
  content: 'string',
  path: 'string',
  tags: 'enum[]',
} as const;

type SearchDoc = {
  id: string;
  kind: DocsSearchResultType;
  pageId: string;
  title: string;
  content: string;
  path: string;
  tags: string[];
  // Unindexed display fields
  breadcrumbs: string[];
  to: string;
  params?: { _splat: string };
  hash?: string;
  method?: string;
  deprecated?: boolean;
};

const MAX_HITS = 60;
const MAX_CHILD_ROWS = 5;

function pageDocs(page: EnginePage): SearchDoc[] {
  const base = {
    pageId: page.slug,
    path: '',
    tags: [],
    breadcrumbs: [] as string[],
    to: '/docs/page/$',
    params: { _splat: page.slug },
  };
  return [
    {
      ...base,
      id: `page:${page.slug}`,
      kind: 'page',
      title: page.title,
      content: `${page.description ?? ''} ${page.keywords ?? ''}`.trim(),
      breadcrumbs: page.breadcrumbs,
    },
    ...page.headings.map(
      (heading): SearchDoc => ({
        ...base,
        id: `heading:${page.slug}#${heading.id}`,
        kind: 'heading',
        title: heading.text,
        content: '',
        hash: heading.id,
      }),
    ),
    ...page.sections.map(
      (section, index): SearchDoc => ({
        ...base,
        id: `text:${page.slug}#${section.headingId ?? ''}:${index}`,
        kind: 'text',
        title: '',
        content: section.text,
        hash: section.headingId ?? undefined,
      }),
    ),
  ];
}

/** Tier-1 breadcrumb labels: bare tag names ("base") are ambiguous on their own. */
export type EngineLabels = { operations: string; schemas: string };
const defaultLabels: EngineLabels = { operations: 'operations', schemas: 'schemas' };

function operationDoc(op: GenOperationSummary, labels: EngineLabels): SearchDoc {
  return {
    id: `operation:${op.hash}`,
    kind: 'operation',
    pageId: `operation:${op.hash}`,
    title: op.summary || op.id,
    content: op.description ?? '',
    path: `${op.method} ${op.path}`,
    tags: op.tags,
    breadcrumbs: [labels.operations, ...op.tags],
    to: '/docs/operations',
    hash: op.hash,
    method: op.method,
    deprecated: op.deprecated,
  };
}

function schemaDoc(schema: GenComponentSchema, labels: EngineLabels): SearchDoc {
  return {
    id: `schema:${schema.name}`,
    kind: 'schema',
    pageId: `schema:${schema.name}`,
    title: schema.name,
    content: schema.description ?? '',
    path: '',
    tags: [schema.schemaTag],
    breadcrumbs: [labels.schemas, schema.schemaTag],
    to: '/docs/schemas',
    // Anchor convention of the schemas page: the $ref path minus its leading '#'.
    hash: schema.ref.replace(/^#/, ''),
  };
}

/** Scope chip → the document kinds it allows. */
const SCOPE_KINDS: Record<Exclude<DocsSearchScope, 'all'>, DocsSearchResultType[]> = {
  pages: ['page', 'heading', 'text'],
  api: ['operation', 'schema'],
};

/** `addOperations`/`addSchemas` accept the API corpus late, as when offline with a cold query cache. */
export function createEngine(
  pages: EnginePage[],
  operations: GenOperationSummary[] | null,
  schemas: GenComponentSchema[] | null = null,
  labels: EngineLabels = defaultLabels,
) {
  const db = create({ schema: searchSchema });
  const docsById = new Map<string, SearchDoc>();

  const insert = (docs: SearchDoc[]) => {
    for (const doc of docs) docsById.set(doc.id, doc);
    // biome-ignore lint/suspicious/noExplicitAny: Orama's insert generics reject unindexed ride-along fields; runtime stores them fine.
    insertMultiple(db, docs as any[]);
  };

  insert(pages.flatMap(pageDocs));
  if (operations) insert(operations.map((op) => operationDoc(op, labels)));
  if (schemas) insert(schemas.map((schema) => schemaDoc(schema, labels)));

  const toRow = (doc: SearchDoc, terms: string[]): DocsSearchResult => ({
    id: doc.id,
    pageId: doc.pageId,
    type: doc.kind,
    title:
      doc.kind === 'text' ? markMatches(trimAroundMatch(doc.content, terms), terms) : markMatches(doc.title, terms),
    breadcrumbs: doc.breadcrumbs,
    to: doc.to,
    params: doc.params,
    hash: doc.hash,
    method: doc.method,
    deprecated: doc.deprecated,
  });

  return {
    addOperations(operations: GenOperationSummary[]) {
      insert(operations.map((op) => operationDoc(op, labels)));
    },

    addSchemas(schemas: GenComponentSchema[]) {
      insert(schemas.map((schema) => schemaDoc(schema, labels)));
    },

    async search(term: string, scope: DocsSearchScope = 'all'): Promise<DocsSearchResult[]> {
      const terms = term.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.length) return [];

      const found = await search(db, {
        term,
        properties: ['title', 'content', 'path'],
        tolerance: 1,
        // 0 = documents must match every term; typo tolerance still applies per term.
        threshold: 0,
        boost: { title: 2, path: 1.5 },
        limit: MAX_HITS,
        ...(scope !== 'all' && { where: { kind: { in: SCOPE_KINDS[scope] } } }),
      });

      // Group hits by page, preserving global score order; a group ranks by its best hit
      const groupOrder: string[] = [];
      const grouped = new Map<string, SearchDoc[]>();
      for (const hit of found.hits) {
        const doc = docsById.get(String(hit.id));
        if (!doc) continue;
        if (!grouped.has(doc.pageId)) {
          grouped.set(doc.pageId, []);
          groupOrder.push(doc.pageId);
        }
        grouped.get(doc.pageId)?.push(doc);
      }

      const rows: DocsSearchResult[] = [];
      for (const pageId of groupOrder) {
        const docs = grouped.get(pageId) ?? [];
        // Standalone rows (operations, schemas): no parent/child structure.
        if (pageId.startsWith('operation:') || pageId.startsWith('schema:')) {
          rows.push(...docs.map((doc) => toRow(doc, terms)));
          continue;
        }
        // Page groups: parent page row first, even when only sections matched, then its heading/text rows
        const pageDoc = docsById.get(`page:${pageId}`);
        if (pageDoc) rows.push(toRow(pageDoc, terms));
        rows.push(
          ...docs
            .filter((doc) => doc.kind !== 'page')
            .slice(0, MAX_CHILD_ROWS)
            .map((doc) => toRow(doc, terms)),
        );
      }
      return rows;
    },
  };
}
